import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
} from "react";
import { REALTIME_SUBSCRIBE_STATES } from "@supabase/supabase-js";
import { supabase, isRealtimeEnabled } from "@/lib/supabase";
import { useAuth } from "./AuthContext";
import { useNotificationStore } from "@/stores/notificationStore";
import { useLessonStore } from "@/stores/lessonStore";
import { useUserStore } from "@/stores/userStore";
import { usePairStore } from "@/stores/pairStore";
import type {
  RealtimeSubscription,
  SubscriptionStatus,
  RealtimeEventHandler,
} from "@/types/realtime";

/**
 * RealtimeContext - Centrálny manager pre Supabase Realtime subscriptions
 * Automaticky spravuje subscriptions pre všetky use cases
 */
interface RealtimeContextType {
  subscriptions: Map<string, RealtimeSubscription>;
  isConnected: boolean;
  error: string | null;
}

const RealtimeContext = createContext<RealtimeContextType>({
  subscriptions: new Map(),
  isConnected: false,
  error: null,
});

export function RealtimeProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<
    Map<string, RealtimeSubscription>
  >(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const channelsRef = useRef<Map<string, ReturnType<typeof supabase.channel>>>(
    new Map(),
  );
  const isCleaningUpRef = useRef(false);
  const hasLoggedRealtimeWarningRef = useRef(false);

  // Store instances - use refs to avoid dependency array issues
  const notificationStoreRef = useRef(useNotificationStore.getState());
  const lessonStoreRef = useRef(useLessonStore.getState());
  const userStoreRef = useRef(useUserStore.getState());
  const pairStoreRef = useRef(usePairStore.getState());

  // Update refs when stores change
  useEffect(() => {
    notificationStoreRef.current = useNotificationStore.getState();
    lessonStoreRef.current = useLessonStore.getState();
    userStoreRef.current = useUserStore.getState();
    pairStoreRef.current = usePairStore.getState();
  });

  /**
   * Helper na vytvorenie subscription
   */
  const createSubscription = (
    channelName: string,
    table: string,
    event: "INSERT" | "UPDATE" | "DELETE" | "*",
    filter: string | undefined,
    handler: RealtimeEventHandler,
  ): void => {
    if (!isRealtimeEnabled) {
      return;
    }

    try {
      // Odstraniť existujúci channel ak existuje
      const existingChannel = channelsRef.current.get(channelName);
      if (existingChannel) {
        void supabase.removeChannel(existingChannel);
        channelsRef.current.delete(channelName);
      }

      // Vytvoriť nový channel
      // Type assertion needed because Supabase types are complex
      const channel = supabase
        .channel(channelName)
        .on(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          "postgres_changes" as any,
          {
            event:
              event === "*"
                ? undefined
                : (event as "INSERT" | "UPDATE" | "DELETE"),
            schema: "public",
            table,
            ...(filter ? { filter } : {}),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handler as any,
        )
        .subscribe((status) => {
          const subscriptionStatus: SubscriptionStatus =
            status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED
              ? "subscribed"
              : status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
                  status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT ||
                  status === REALTIME_SUBSCRIBE_STATES.CLOSED
                ? "error"
                : "connecting";

          setSubscriptions((prev) => {
            const newMap = new Map(prev);
            newMap.set(channelName, {
              channelName,
              status: subscriptionStatus,
              error:
                status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
                status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT ||
                status === REALTIME_SUBSCRIBE_STATES.CLOSED
                  ? "Subscription error"
                  : null,
            });
            return newMap;
          });

          if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
            setIsConnected(true);
            setError(null);
          } else if (
            status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
            status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT
          ) {
            // Ignorovať CLOSED status počas cleanupu - to je očakávané správanie
            if (!isCleaningUpRef.current) {
              // Realtime errors sú očakávané ak Realtime nie je povolené v Supabase Cloud
              // Logovať len raz, nie pre každý channel
              if (import.meta.env.DEV && !hasLoggedRealtimeWarningRef.current) {
                hasLoggedRealtimeWarningRef.current = true;
                console.warn(
                  "Realtime subscription errors detected. This is expected if Realtime is not enabled in Supabase Cloud. App will work without real-time updates.",
                );
              }
              // Nezobrazovať error v UI - aplikácia funguje aj bez Realtime
            }
          } else if (status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
            // CLOSED status je očakávaný počas cleanupu, ignorovať ho
            if (!isCleaningUpRef.current && import.meta.env.DEV) {
              console.debug(`Realtime channel ${channelName} closed`);
            }
          }
        });

      channelsRef.current.set(channelName, channel);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      if (import.meta.env.DEV) {
        console.error(
          `Failed to create subscription ${channelName}:`,
          errorMessage,
        );
      }
    }
  };

  /**
   * Setup všetkých subscriptions
   */
  useEffect(() => {
    if (!isRealtimeEnabled || !user) {
      // Cleanup ak nie je user alebo Realtime nie je povolené
      isCleaningUpRef.current = true;
      channelsRef.current.forEach((channel) => {
        void supabase.removeChannel(channel);
      });
      channelsRef.current.clear();
      setSubscriptions(new Map());
      setIsConnected(false);
      hasLoggedRealtimeWarningRef.current = false; // Reset warning flag
      setTimeout(() => {
        isCleaningUpRef.current = false;
      }, 100);
      return;
    }

    const userId = user.id;
    const isAdmin = user.role === "admin";

    // Notifications: lazy subscription cez notificationStore.subscribeToNotifications
    // (volané z Dashboard a NotificationsPage pri mounte)

    // 2. Group Lessons subscription
    createSubscription("group_lessons", "group_lessons", "*", undefined, () => {
      // Refresh group lessons
      lessonStoreRef.current.handleLessonChange();
    });

    // 3. Group Signups subscription
    createSubscription("group_signups", "group_signups", "*", undefined, () => {
      // Refresh signups a lessons (pre kapacitu)
      void lessonStoreRef.current.handleSignupChange();
    });

    // 4. Private Lessons subscription
    createSubscription(
      "private_lessons",
      "private_lessons",
      "*",
      `user_id=eq.${userId}`,
      () => {
        // Refresh private lessons
        void lessonStoreRef.current.handlePrivateLessonChange();
      },
    );

    // 5. Transactions subscription
    createSubscription(
      "transactions",
      "transactions",
      "*",
      `user_id=eq.${userId}`,
      () => {
        // Refresh transactions a hodiny (pre aktualizáciu balíkov)
        void userStoreRef.current.handleTransactionChange();
      },
    );

    // 5b. User Hours subscription - pre priamy update user_hours tabuľky
    createSubscription(
      "user_hours",
      "user_hours",
      "*",
      `user_id=eq.${userId}`,
      () => {
        // Refresh hodiny a dashboard pri zmene user_hours
        void userStoreRef.current.fetchHours(userId);
        void userStoreRef.current.fetchDashboard(userId);
      },
    );

    // 6. Pairs subscriptions (potrebujeme dva, lebo OR filter nie je podporovaný)
    // Subscription pre páry kde user je user_a
    createSubscription(
      "pairs_user_a",
      "pairs",
      "*",
      `user_a_id=eq.${userId}`,
      () => {
        // Refresh pairs
        void pairStoreRef.current.handlePairChange();
      },
    );

    // Subscription pre páry kde user je user_b
    createSubscription(
      "pairs_user_b",
      "pairs",
      "*",
      `user_b_id=eq.${userId}`,
      () => {
        // Refresh pairs
        void pairStoreRef.current.handlePairChange();
      },
    );

    // 7. Availability Slots subscription
    createSubscription(
      "availability_slots",
      "availability_slots",
      "*",
      undefined,
      () => {
        // Availability sa aktualizuje cez callback v komponente
        // Toto subscription len notifikuje, komponent si data načíta sám
      },
    );

    // 8. Admin Stats subscriptions (len pre admin)
    if (isAdmin) {
      // Users
      createSubscription("admin_users", "users", "*", undefined, () => {
        // Admin stats sa aktualizujú cez callback v Dashboard komponente
      });

      // Packages
      createSubscription("admin_packages", "packages", "*", undefined, () => {
        // Admin stats sa aktualizujú cez callback v Dashboard komponente
      });
    }

    // Cleanup function
    return () => {
      // Nastaviť flag, že cleanup prebieha - toto zabráni logovaniu CLOSED ako chyby
      isCleaningUpRef.current = true;

      // Copy current channels to avoid stale closure issues
      // Store ref value in local variable to avoid stale closure warning
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const channelsAtCleanup = channelsRef.current;
      const currentChannels = new Map(channelsAtCleanup);
      currentChannels.forEach((channel) => {
        void supabase.removeChannel(channel);
      });
      channelsAtCleanup.clear();
      setSubscriptions(new Map());
      setIsConnected(false);
      hasLoggedRealtimeWarningRef.current = false; // Reset warning flag

      // Reset flag po malom delay, pre prípad že by sa callback volal asynchrónne
      setTimeout(() => {
        isCleaningUpRef.current = false;
      }, 100);
    };
  }, [user]); // Stores sú v refs, nie sú potrebné v dependencies

  return (
    <RealtimeContext.Provider
      value={{
        subscriptions,
        isConnected,
        error,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime(): RealtimeContextType {
  return useContext(RealtimeContext);
}
